import { useMutation } from '@apollo/client'
import {
    Button,
    Input,
    Textarea,
    FormControl,
    FormLabel,
    VStack,
    Text,
    useToast
} from '@chakra-ui/react'
import campaign from 'graphql/mutations/campaign'
import { Campaign } from 'graphql/types'
import * as React from 'react'

interface Props {
    Campaign: Campaign | undefined
    setEditCampaign: (Campaign: Campaign | null) => void
}

export const EditCampaign = ({Campaign, setEditCampaign}: Props) => {
    const toast = useToast()

    const [mutateCampaign, { loading }] = useMutation(campaign, {
        refetchQueries: ['getCampaigns'],
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true
            })
        },
        onCompleted: () => {
            toast({
                title: 'Success',
                description: 'Campaign saved successfully',
                status: 'success',
                duration: 3000,
                isClosable: true
            })
            setEditCampaign(null)
        }
    })

    const [newID, setNewID] = React.useState(Campaign?.ID || '')
    const [description, setDescription] = React.useState(Campaign?.Description || '')
    const [name, setName] = React.useState(Campaign?.Name || '')
    const [priority, setPriority] = React.useState(Campaign?.Priority || '')

    const handleSave = async () => {
        await mutateCampaign({
            variables: {
                CampaignID: Campaign?.ID || newID,
                Description: description,
                Name: name,
                Priority: priority
            }
        })
    }

    const isNewCampaign = !Campaign || Campaign.ID === ''

    return (
        <VStack spacing={4} align="stretch" p={6} minW="500px">
            <Text fontSize="xl" fontWeight="bold">
                {isNewCampaign ? 'Create New Campaign' : 'Edit Campaign'}
            </Text>

            <FormControl isRequired>
                <FormLabel>ID</FormLabel>
                {isNewCampaign ? (
                    <Input
                        value={newID}
                        onChange={(e) => setNewID(e.currentTarget.value)}
                        placeholder="Enter campaign ID"
                    />
                ) : (
                    <Text fontWeight="medium">{Campaign.ID}</Text>
                )}
            </FormControl>

            <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    placeholder="Enter campaign name"
                />
            </FormControl>

            <FormControl>
                <FormLabel>Priority</FormLabel>
                <Input
                    type="number"
                    value={priority}
                    onChange={(e) => setPriority(e.currentTarget.value)}
                    placeholder="Enter priority (higher = more important)"
                />
            </FormControl>

            <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.currentTarget.value)}
                    placeholder="Enter campaign description"
                    rows={5}
                />
            </FormControl>

            <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={loading}
                loadingText="Saving..."
                size="lg"
            >
                Save Campaign
            </Button>
        </VStack>
    )
}
